import React, { useState, useEffect, useRef } from 'react';
import wearService from './wearService';
import { calendar, notes } from './api';
import LessonEditModal from './components/LessonEditModal';
import LoadingSkeleton from './components/LoadingSkeleton';

function TodayLessons({ onRefresh, onLessonClick, lessonNotes }) {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [draggedLesson, setDraggedLesson] = useState(null);

  // Carousel state - now for month view
  const [currentIndex, setCurrentIndex] = useState(0);
  const [monthDates, setMonthDates] = useState([]);
  const [displayMonth, setDisplayMonth] = useState(new Date());
  const [monthNavigationIndex, setMonthNavigationIndex] = useState(0);

  // Month picker modal state
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [pickerMonth, setPickerMonth] = useState(new Date());

  // Lesson editing state
  const [editingLesson, setEditingLesson] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const lessonsRef = useRef([]);
  const lessonNotesRef = useRef({});
  const carouselRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const currentTranslate = useRef(0);
  const prevTranslate = useRef(0);
  const animationRef = useRef(null);

  // Component mounted
  useEffect(() => {
  }, []);

  // Generate all days in the current/displayed month
  const generateMonthDates = (date = new Date()) => {
    const dates = [];
    const year = date.getFullYear();
    const month = date.getMonth();

    // Get the first day of the month
    const firstDay = new Date(year, month, 1);
    // Get the last day of the month
    const lastDay = new Date(year, month + 1, 0);

    for (let i = 1; i <= lastDay.getDate(); i++) {
      const day = new Date(year, month, i);
      dates.push(day);
    }

    return dates;
  };

  // Check if next month should be available (within 7 days of month end)
  const isNextMonthAvailable = () => {
    const today = new Date();
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const daysUntilNextMonth = lastDayOfMonth.getDate() - today.getDate();
    return daysUntilNextMonth <= 7;
  };

  // Find today's index in the month dates
  const getTodayIndex = (dates) => {
    const today = new Date();
    return dates.findIndex(date => isSameDay(date, today));
  };

  // Initialize month view
  useEffect(() => {
    const today = new Date();
    const displayDate = new Date(today.getFullYear(), today.getMonth() + monthNavigationIndex, 1);
    setDisplayMonth(displayDate);

    const dates = generateMonthDates(displayDate);
    setMonthDates(dates);

    // If viewing current month, start at today
    let newIndex;
    if (monthNavigationIndex === 0) {
      const todayIdx = getTodayIndex(dates);
      newIndex = todayIdx >= 0 ? todayIdx : 0;
    } else {
      // If viewing other month, start at first day
      newIndex = 0;
    }

    setCurrentIndex(newIndex);
    // Don't set selectedDate here - let the currentIndex effect handle it
    // to avoid duplicate updates and duplicate fetches
  }, [monthNavigationIndex]);

  // Update carousel position when currentIndex changes (without triggering extra fetches)
  useEffect(() => {
    setCarouselPosition();
  }, [currentIndex]);

  const fetchLessonsForDate = async (date) => {
    try {
      setLoading(true);

      // Format date as YYYY-MM-DD
      const dateStr = date.toISOString().split('T')[0];

      // Call backend to fetch calendar events using service account
      const response = await calendar.getEventsForDate(date);
      const fetchedLessons = response.data.events || [];

      if (fetchedLessons.length > 0) {
        console.log(`📅 TodayLessons: Fetched ${fetchedLessons.length} lessons for ${dateStr}`);
      }
      setLessons(fetchedLessons);
      setError(null);
      setLoading(false);

      // Sync to watch - both player and coach lessons should sync when fetched
      // Coach needs reminders for their own lessons when viewing their calendar
      try {
        if (fetchedLessons.length > 0) {
          console.log(`📱 Syncing ${fetchedLessons.length} lessons to watch...`);
          const syncResult = await wearService.sendLessonsToWatch(fetchedLessons, lessonNotes);
          console.log(`✅ Watch sync result:`, syncResult);
        } else {
          console.log(`⚠️ No lessons to sync for ${dateStr}`);
        }
      } catch (watchError) {
        console.error(`❌ Watch sync error:`, watchError);
      }

      return fetchedLessons;
    } catch (error) {
      console.error('❌ Error fetching calendar events:', error);
      setError('Failed to load calendar events. Please try again.');
      setLoading(false);
      return [];
    }
  };

  const isSameDay = (date1, date2) => {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  };

  const isToday = (date) => {
    return isSameDay(date, new Date());
  };

  // Sync a week's worth of lessons to watch for offline access
  const syncWeekOfLessonsToWatch = async () => {
    try {
      const today = new Date();

      // Fetch lessons for today + next 6 days, grouped by date
      for (let i = 0; i < 7; i++) {
        const date = new Date(today);
        date.setDate(date.getDate() + i);

        try {
          const response = await calendar.getEventsForDate(date);
          const dayLessons = response.data.events || [];
          // Send each day's lessons separately with the correct date label
          if (dayLessons.length > 0) {
            await wearService.sendLessonsToWatch(dayLessons, lessonNotes);
          }
        } catch (err) {
          console.error(`⚠️ Failed to fetch lessons for ${date.toDateString()}:`, err);
        }
      }

    } catch (error) {
      console.error('❌ Error syncing week to watch:', error);
    }
  };

  const formatDayName = (date) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[date.getDay()];
  };

  const formatDateNumber = (date) => {
    return date.getDate();
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Carousel Animation with proper centering
  const setCarouselPosition = (animate = true) => {
    if (carouselRef.current) {
      const containerWidth = carouselRef.current.parentElement.offsetWidth;
      const itemWidth = containerWidth; // Each item takes full width
      const centerOffset = 0; // No offset needed since items are full width
      const offset = -(currentIndex * itemWidth) + centerOffset;
      
      currentTranslate.current = offset;
      
      if (animate) {
        carouselRef.current.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      } else {
        carouselRef.current.style.transition = 'none';
      }
      
      carouselRef.current.style.transform = `translateX(${offset}px)`;
    }
  };

  const snapToNearestDate = () => {
    if (!carouselRef.current) return;

    const containerWidth = carouselRef.current.parentElement.offsetWidth;
    const itemWidth = containerWidth;
    const currentOffset = currentTranslate.current;
    const velocity = currentOffset - prevTranslate.current;

    // Ultra-sensitive: Just 10% swipe needed
    const swipeThreshold = itemWidth * 0.10;

    let newIndex = currentIndex;

    if (velocity > swipeThreshold) {
      // Swiped right
      newIndex = currentIndex - 1;
    } else if (velocity < -swipeThreshold) {
      // Swiped left
      newIndex = currentIndex + 1;
    } else {
      // Barely moved - stay on current
      newIndex = currentIndex;
    }

    // Handle month navigation when swiping past boundaries
    if (newIndex < 0) {
      // Swiped left past first day - go to previous month
      setMonthNavigationIndex(prev => prev - 1);
      newIndex = monthDates.length - 1; // Start from last day of previous month
    } else if (newIndex >= monthDates.length) {
      // Swiped right past last day - go to next month (if available)
      if (monthNavigationIndex === 0 && isNextMonthAvailable()) {
        setMonthNavigationIndex(prev => prev + 1);
        newIndex = 0; // Start from first day of next month
      } else if (monthNavigationIndex > 0) {
        // Already viewing a future month, allow navigation
        setMonthNavigationIndex(prev => prev + 1);
        newIndex = 0;
      } else {
        // Next month not available yet, stay on last day of current month
        newIndex = monthDates.length - 1;
      }
    }

    setCurrentIndex(Math.max(0, Math.min(newIndex, monthDates.length - 1)));
  };

  // Touch handlers
  const handleTouchStart = (e) => {
    isDragging.current = true;
    startX.current = e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
    prevTranslate.current = currentTranslate.current;
    
    if (carouselRef.current) {
      carouselRef.current.style.transition = 'none';
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;

    const currentPosition = e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
    const diff = currentPosition - startX.current;
    
    currentTranslate.current = prevTranslate.current + diff;

    if (carouselRef.current) {
      carouselRef.current.style.transform = `translateX(${currentTranslate.current}px)`;
    }
  };

  const handleTouchEnd = () => {
    isDragging.current = false;
    snapToNearestDate();
  };

  // Mouse handlers for desktop
  const handleMouseDown = (e) => {
    e.preventDefault();
    handleTouchStart(e);
  };

  const handleMouseMove = (e) => {
    if (isDragging.current) {
      e.preventDefault();
      handleTouchMove(e);
    }
  };

  const handleMouseUp = () => {
    if (isDragging.current) {
      handleTouchEnd();
    }
  };

  const handleMouseLeave = () => {
    if (isDragging.current) {
      handleTouchEnd();
    }
  };

  useEffect(() => {
    setCarouselPosition();
    // Update selected date when currentIndex changes (for within-month swiping)
    // This is separate from monthNavigationIndex changes which are handled in the month init effect
    if (monthDates.length > 0 && monthDates[currentIndex]) {
      setSelectedDate(monthDates[currentIndex]);
    }
  }, [currentIndex, monthDates]);

  useEffect(() => {
    if (selectedDate) {
      setLoading(true);
      fetchLessonsForDate(selectedDate);
    }
  }, [selectedDate]);

  useEffect(() => {
    lessonsRef.current = lessons;
    lessonNotesRef.current = lessonNotes;
  }, [lessons, lessonNotes]);

  useEffect(() => {
    window.triggerWatchSync = () => {
      if (lessonsRef.current.length > 0 && isToday(selectedDate)) {
        wearService.sendLessonsToWatch(lessonsRef.current, lessonNotesRef.current);
      }
    };

    return () => {
      delete window.triggerWatchSync;
    };
  }, [selectedDate]);

  // Auto-sync to watch every 30 seconds
  useEffect(() => {
    const syncInterval = setInterval(() => {
      if (lessons.length > 0 && isToday(selectedDate)) {
        wearService.sendLessonsToWatch(lessons, lessonNotes);
      }
    }, 30000);

    return () => clearInterval(syncInterval);
  }, [lessons, lessonNotes, selectedDate]);

  // Auto-refresh today's lessons every 5 minutes when viewing today
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      if (isToday(selectedDate)) {
        fetchLessonsForDate(selectedDate);
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(refreshInterval);
  }, [selectedDate]);

  useEffect(() => {
    if (onRefresh) {
      onRefresh(() => fetchLessonsForDate(selectedDate));
    }
  }, [onRefresh, selectedDate]);

  useEffect(() => {
    if (lessons.length > 0 && lessonNotes && isToday(selectedDate)) {
      wearService.sendLessonsToWatch(lessons, lessonNotes)
        .catch(error => console.error('Watch sync error:', error));
    }
  }, [lessonNotes, selectedDate]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    window.receiveLessonDrop = (lessonId) => {
      const lesson = lessons.find(l => l.id === lessonId);
      if (!lesson) return null;

      const note = lessonNotes?.[lessonId] || '';
      const startTime = formatTime(lesson.start?.dateTime);
      const endTime = formatTime(lesson.end?.dateTime);
      
      const lessonNote = {
        id: Date.now().toString(),
        text: `📚 Lesson: ${startTime} - ${endTime}\n${lesson.summary || 'Untitled Lesson'}${note ? '\n\n' + note : ''}`,
        timestamp: new Date().toISOString(),
        fromLesson: true,
        lessonId: lessonId,
        lessonTime: `${startTime} - ${endTime}`,
        lessonTitle: lesson.summary || 'Untitled Lesson'
      };

      return lessonNote;
    };

    return () => {
      delete window.receiveLessonDrop;
    };
  }, [lessons, lessonNotes]);

  const isLessonPast = (lesson) => {
    if (!lesson.start || !lesson.start.dateTime) return false;
    const lessonEndTime = lesson.end ? new Date(lesson.end.dateTime) : new Date(lesson.start.dateTime);
    return currentTime > lessonEndTime;
  };

  const hasLessonNote = (lessonId) => {
    return lessonNotes && lessonNotes[lessonId];
  };

  const formatTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    const date = new Date(dateTimeString);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Handle successful lesson update (edit)
  const handleEditSuccess = (updatedEvent) => {

    // Normalize the event data - backend returns start/end as objects
    const normalizeEventData = (event) => {
      let start = event.start;
      let end = event.end;

      // If start/end are objects with dateTime property (from Google Calendar API)
      if (typeof start === 'object' && start.dateTime) {
        start = start.dateTime;
      }
      if (typeof end === 'object' && end.dateTime) {
        end = end.dateTime;
      }

      return {
        id: event.id || editingLesson.id,
        summary: event.summary || editingLesson.summary,
        description: event.description !== undefined ? event.description : (editingLesson.description || ''),
        start: start || editingLesson.start,
        end: end || editingLesson.end,
        location: event.location !== undefined ? event.location : (editingLesson.location || '')
      };
    };

    const normalizedEvent = normalizeEventData(updatedEvent);

    const updatedLessons = lessons.map(lesson =>
      lesson.id === editingLesson.id ? normalizedEvent : lesson
    );

    setLessons(updatedLessons);

    // Sync updated lessons to watch if viewing today
    if (isToday(selectedDate)) {
      wearService.sendLessonsToWatch(updatedLessons, lessonNotes)
        .catch(error => console.error('❌ Watch sync error after edit:', error));
    }
  };

  // Handle lesson deletion
  const handleDeleteSuccess = (deletedEvent) => {

    const updatedLessons = lessons.filter(lesson => lesson.id !== editingLesson.id);
    setLessons(updatedLessons);

    // Sync updated lessons to watch if viewing today
    if (isToday(selectedDate)) {
      wearService.sendLessonsToWatch(updatedLessons, lessonNotes)
        .catch(error => console.error('❌ Watch sync error after delete:', error));
    }

    // Show delete confirmation
    const deleteMessage = `✅ "${editingLesson.summary}" deleted`;
    alert(deleteMessage);
  };

  const handleDragStart = (e, lesson) => {
    setDraggedLesson(lesson);
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', lesson.id);
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedLesson(null);
  };

  const upcomingLessons = lessons.filter(lesson => !isLessonPast(lesson));
  const completedLessons = lessons.filter(lesson => isLessonPast(lesson));

  const LessonCard = ({ lesson, isPast }) => {
    const hasNote = hasLessonNote(lesson.id);
    
    return (
      <div
        className="today-lesson-card"
        draggable="true"
        onDragStart={(e) => handleDragStart(e, lesson)}
        onDragEnd={handleDragEnd}
        onClick={() => onLessonClick && onLessonClick(lesson)}
        style={{
          padding: '12px',
          background: isPast ? '#ffe6e6' : '#e8f5e9',
          borderRadius: '5px',
          borderLeft: isPast ? '4px solid #e74c3c' : '4px solid #27ae60',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          opacity: isPast ? 0.8 : 1,
          transition: 'all 0.3s ease',
          cursor: 'grab',
          marginBottom: '10px',
          position: 'relative',
          userSelect: 'none'
        }}
        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
        onMouseLeave={(e) => e.currentTarget.style.opacity = isPast ? '0.8' : '1'}
      >
        <div style={{ flex: 1 }}>
          <div style={{
            fontWeight: 'bold',
            marginBottom: '5px',
            color: isPast ? '#c0392b' : '#2c3e50',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            {lesson.summary || 'Untitled Lesson'}
            {hasNote && (
              <span style={{
                background: '#3498db',
                color: 'white',
                fontSize: '10px',
                padding: '2px 6px',
                borderRadius: '3px',
                fontWeight: 'bold'
              }}>
                📝 NOTE
              </span>
            )}
          </div>
          {lesson.description && (
            <div style={{
              fontSize: '12px',
              color: '#7f8c8d',
              marginBottom: '5px'
            }}>
              {lesson.description}
            </div>
          )}
          {lesson.location && (
            <div style={{
              fontSize: '12px',
              color: '#7f8c8d'
            }}>
              📍 {lesson.location}
            </div>
          )}

          {/* Start and End Times with Icons */}
          <div style={{
            display: 'flex',
            gap: '16px',
            marginTop: '8px',
            fontSize: '12px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color: isPast ? '#c0392b' : '#27ae60',
              fontWeight: '600'
            }}>
              <span style={{ fontSize: '14px' }}>🕐</span>
              <span>{formatTime(lesson.start.dateTime)}</span>
            </div>

            {lesson.end && lesson.end.dateTime && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: isPast ? '#c0392b' : '#27ae60',
                fontWeight: '600'
              }}>
                <span style={{ fontSize: '14px' }}>🕑</span>
                <span>{formatTime(lesson.end.dateTime)}</span>
              </div>
            )}
          </div>

          <div style={{
            fontSize: '11px',
            color: '#5f6b6c',
            fontStyle: 'italic',
            marginTop: '6px'
          }}>
            💡 Drag to student to add to their notes
          </div>
        </div>

        {/* Edit Button - Only for upcoming lessons */}
        {!isPast && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setEditingLesson(lesson);
              setShowEditModal(true);
            }}
            style={{
              padding: '6px 12px',
              background: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '12px',
              marginLeft: '12px',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
            title="Edit this lesson"
          >
            ✏️ Edit
          </button>
        )}
      </div>
    );
  };

  return (
    <div style={{
      background: 'white',
      borderRadius: '8px',
      marginBottom: '20px',
      overflow: 'hidden'
    }}>
      {/* Month Navigation Header */}
      <div style={{
        padding: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: '#f8f9fa',
        borderBottom: '1px solid #e0e0e0',
        fontSize: '14px',
        fontWeight: '600',
        color: '#2c3e50'
      }}>
        {/* Previous Month Button */}
        <button
          onClick={() => setMonthNavigationIndex(prev => prev - 1)}
          disabled={monthNavigationIndex === 0}
          style={{
            background: monthNavigationIndex === 0 ? '#ecf0f1' : '#2f4ba2',
            color: monthNavigationIndex === 0 ? '#bdc3c7' : 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '6px 12px',
            cursor: monthNavigationIndex === 0 ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            opacity: monthNavigationIndex === 0 ? 0.5 : 1
          }}
        >
          ← Prev
        </button>

        {/* TAPPABLE MONTH - OPEN MONTH PICKER */}
        <div
          onClick={() => setShowMonthPicker(true)}
          onTouchEnd={(e) => {
            e.stopPropagation();
            setShowMonthPicker(true);
          }}
          style={{
            flex: 1,
            textAlign: 'center',
            cursor: 'pointer',
            padding: '8px 12px',
            borderRadius: '6px',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            WebkitTouchCallout: 'none',
            transition: 'background-color 0.2s ease',
            backgroundColor: 'transparent'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#e0e0e0';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          title="Tap to open month picker"
        >
          📅 {displayMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </div>

        {/* Next Month Button - Available 7 days before month end */}
        <button
          onClick={() => setMonthNavigationIndex(prev => prev + 1)}
          disabled={monthNavigationIndex === 0 && !isNextMonthAvailable()}
          style={{
            background: (monthNavigationIndex === 0 && !isNextMonthAvailable()) ? '#ecf0f1' : '#2f4ba2',
            color: (monthNavigationIndex === 0 && !isNextMonthAvailable()) ? '#bdc3c7' : 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '6px 12px',
            cursor: (monthNavigationIndex === 0 && !isNextMonthAvailable()) ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            opacity: (monthNavigationIndex === 0 && !isNextMonthAvailable()) ? 0.5 : 1
          }}
        >
          Next →
        </button>
      </div>

      {/* Date Carousel - Full Month with Swipe Navigation */}
      <div 
        style={{
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)',
          userSelect: 'none',
          cursor: isDragging.current ? 'grabbing' : 'grab',
          touchAction: 'pan-y',
          height: '180px'
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <div
          ref={carouselRef}
          style={{
            display: 'flex',
            height: '100%',
            willChange: 'transform'
          }}
        >
          {monthDates.map((date, index) => {
            const isCurrent = index === currentIndex;
            const today = isToday(date);

            return (
              <div
                key={index}
                style={{
                  minWidth: '100%',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '20px',
                  pointerEvents: 'none'
                }}
              >
                <div style={{
                  fontSize: '16px',
                  color: 'rgba(255, 255, 255, 0.98)',
                  fontWeight: '600',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '2px'
                }}>
                  {formatDayName(date)}
                  {today && ' • Today'}
                </div>
                <div style={{
                  fontSize: '72px',
                  fontWeight: '700',
                  color: 'white',
                  lineHeight: '1',
                  textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                  cursor: isToday(date) ? 'default' : 'pointer',
                  userSelect: 'none',
                  padding: '10px',
                  borderRadius: '12px',
                  transition: 'transform 0.1s ease',
                  pointerEvents: 'auto',
                  WebkitTouchCallout: 'none',
                  WebkitUserSelect: 'none'
                }}>
                  <div
                    onClick={() => {
                      if (!isToday(date)) {
                        // Go back to today
                        setMonthNavigationIndex(0);
                        const todayIdx = getTodayIndex(monthDates.length > 0 ? monthDates : generateMonthDates());
                        if (todayIdx >= 0) setCurrentIndex(todayIdx);
                      }
                    }}
                    onTouchEnd={(e) => {
                      // Prevent touch event from propagating to carousel
                      e.stopPropagation();
                    }}
                    style={{
                      cursor: isToday(date) ? 'default' : 'pointer'
                    }}
                    title={isToday(date) ? 'Already on today' : 'Tap to return to today'}
                  >
                    {formatDateNumber(date)}
                  </div>
                </div>
                <div style={{
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.96)',
                  marginTop: '8px',
                  fontWeight: '500'
                }}>
                  {formatMonthYear(date)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Week dots indicator */}
        <div style={{
          position: 'absolute',
          bottom: '15px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '8px'
        }}>
          {monthDates.map((_, index) => (
            <div
              key={index}
              style={{
                width: index === currentIndex ? '24px' : '8px',
                height: '8px',
                borderRadius: '4px',
                background: index === currentIndex 
                  ? 'white' 
                  : 'rgba(255, 255, 255, 0.4)',
                transition: 'all 0.3s ease'
              }}
            />
          ))}
        </div>
      </div>

      {/* Lessons Content */}
      <div style={{ padding: '20px' }}>
        <h3 style={{ 
          marginBottom: '15px', 
          color: '#2c3e50',
          fontSize: '18px',
          fontWeight: '600'
        }}>
          {lessons.length} {lessons.length === 1 ? 'lesson' : 'lessons'}
        </h3>
        
        {loading ? (
          <div style={{ padding: '20px 0' }}>
            <LoadingSkeleton type="list" count={4} />
          </div>
        ) : error ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#e74c3c'
          }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '15px'
            }}>
              ⚠️
            </div>
            <p style={{ marginBottom: '15px', fontSize: '16px' }}>
              {error}
            </p>
            <button
              onClick={() => fetchLessonsForDate(selectedDate)}
              style={{
                padding: '10px 20px',
                background: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              🔄 Retry
            </button>
          </div>
        ) : lessons.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px',
            color: '#95a5a6'
          }}>
            <div style={{ 
              fontSize: '48px',
              marginBottom: '10px'
            }}>📅</div>
            No lessons scheduled
          </div>
        ) : (
          <>
            {upcomingLessons.length > 0 && (
              <div>
                <h4 style={{ 
                  fontSize: '14px', 
                  color: '#27ae60', 
                  marginBottom: '10px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  Upcoming ({upcomingLessons.length})
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {upcomingLessons.map((lesson) => (
                    <LessonCard key={lesson.id} lesson={lesson} isPast={false} />
                  ))}
                </div>
              </div>
            )}

            {completedLessons.length > 0 && (
              <div style={{ marginTop: upcomingLessons.length > 0 ? '20px' : '0' }}>
                <h4 style={{ 
                  fontSize: '14px', 
                  color: '#e74c3c', 
                  marginBottom: '10px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  Completed ({completedLessons.length})
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {completedLessons.map((lesson) => (
                    <LessonCard key={lesson.id} lesson={lesson} isPast={true} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* MONTH PICKER MODAL */}
      {showMonthPicker && (
        <div
          onClick={() => setShowMonthPicker(false)}
          onTouchEnd={(e) => {
            if (e.target === e.currentTarget) setShowMonthPicker(false);
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              maxWidth: '90vw',
              width: '400px',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '20px',
                color: '#2c3e50'
              }}>
                Select Date
              </h2>
              <button
                onClick={() => setShowMonthPicker(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: 0,
                  width: '30px',
                  height: '30px'
                }}
              >
                ✕
              </button>
            </div>

            {/* Month/Year Navigation in Modal */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <button
                onClick={() => setPickerMonth(new Date(pickerMonth.getFullYear(), pickerMonth.getMonth() - 1, 1))}
                style={{
                  background: '#2f4ba2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                ← Prev
              </button>
              <div style={{
                fontWeight: '600',
                color: '#2c3e50',
                minWidth: '150px',
                textAlign: 'center'
              }}>
                {pickerMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </div>
              <button
                onClick={() => setPickerMonth(new Date(pickerMonth.getFullYear(), pickerMonth.getMonth() + 1, 1))}
                style={{
                  background: '#2f4ba2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Next →
              </button>
            </div>

            {/* Calendar Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: '8px'
            }}>
              {/* Day headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div
                  key={day}
                  style={{
                    textAlign: 'center',
                    fontWeight: '600',
                    color: '#2f4ba2',
                    fontSize: '12px',
                    padding: '8px 0'
                  }}
                >
                  {day}
                </div>
              ))}

              {/* Date cells */}
              {(() => {
                const year = pickerMonth.getFullYear();
                const month = pickerMonth.getMonth();
                const firstDay = new Date(year, month, 1).getDay();
                const daysInMonth = new Date(year, month + 1, 0).getDate();
                const today = new Date();
                const cells = [];

                // Empty cells
                for (let i = 0; i < firstDay; i++) {
                  cells.push(
                    <div key={`empty-${i}`} style={{ padding: '8px' }} />
                  );
                }

                // Date cells
                for (let day = 1; day <= daysInMonth; day++) {
                  const date = new Date(year, month, day);
                  const isTodayCell = isSameDay(date, today);
                  const isSelectedCell = isSameDay(date, selectedDate);

                  cells.push(
                    <button
                      key={day}
                      onClick={() => {
                        // Navigate to the selected date
                        const dateIndex = monthDates.findIndex(d => isSameDay(d, date));
                        if (dateIndex !== -1) {
                          setCurrentIndex(dateIndex);
                        } else {
                          const diffMonths = (year - displayMonth.getFullYear()) * 12 + (month - displayMonth.getMonth());
                          setMonthNavigationIndex(prev => prev + diffMonths);
                        }
                        setShowMonthPicker(false);
                      }}
                      style={{
                        padding: '10px 8px',
                        border: isSelectedCell ? '2px solid #2f4ba2' : isTodayCell ? '2px solid #FF6B6B' : '1px solid #e0e0e0',
                        borderRadius: '6px',
                        background: isSelectedCell ? '#2f4ba2' : isTodayCell ? '#FFE8E8' : '#f8f9fa',
                        color: isSelectedCell ? 'white' : isTodayCell ? '#FF6B6B' : '#2c3e50',
                        cursor: 'pointer',
                        fontWeight: isTodayCell ? '600' : '500',
                        fontSize: '14px',
                        transition: 'all 0.2s ease',
                        WebkitTouchCallout: 'none'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelectedCell) e.currentTarget.style.background = '#e0e0e0';
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelectedCell) e.currentTarget.style.background = isTodayCell ? '#FFE8E8' : '#f8f9fa';
                      }}
                    >
                      {day}
                    </button>
                  );
                }

                return cells;
              })()}
            </div>

            {/* Today button */}
            <button
              onClick={() => {
                const today = new Date();
                setPickerMonth(today);
                setMonthNavigationIndex(0);
                const todayIdx = getTodayIndex(generateMonthDates());
                if (todayIdx >= 0) setCurrentIndex(todayIdx);
                setShowMonthPicker(false);
              }}
              style={{
                width: '100%',
                marginTop: '16px',
                padding: '10px 16px',
                background: '#27ae60',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              ↩️ Go to Today
            </button>
          </div>
        </div>
      )}

      {/* Lesson Edit Modal */}
      {showEditModal && editingLesson && (
        <LessonEditModal
          lesson={editingLesson}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={handleEditSuccess}
          onDelete={handleDeleteSuccess}
        />
      )}
    </div>
  );
}

export default TodayLessons;